import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    MessageCircle,
    MoreHorizontal,
    Archive,
    ArchiveRestore,
    Trash2,
    Pencil,
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { getOrCreateConversation } from '../../../firebase/conversation.service';
import { formatMessageTime } from '../../../utils/inboxHelpers';

/**
 * Individual lead row for the desktop table view.
 * Context menu actions are fully functional.
 */
export const LeadRow = ({
    lead,
    executions = [],
    openLeadDrawer,
    openMenu,
    toggleMenu,
    onArchiveLead,
    onUnarchiveLead,
    onDeleteLead,
}) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleEdit = (e) => {
        e.stopPropagation();
        openLeadDrawer(lead); // Open drawer in edit mode
    };

    const handleArchive = async (e) => {
        e.stopPropagation();
        try {
            if (lead.archived) {
                await onUnarchiveLead?.(lead.id);
            } else {
                await onArchiveLead?.(lead.id);
            }
        } catch (error) {
            console.error('Error archiving lead:', error);
        }
    };

    const handleDeleteClick = (e) => {
        e.stopPropagation();
        setShowDeleteConfirm(true);
    };

    const handleDelete = async (e) => {
        e.stopPropagation();
        setIsDeleting(true);
        try {
            await onDeleteLead?.(lead.id);
        } catch (error) {
            console.error('Error deleting lead:', error);
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    return (
        <>
            <tr
                key={lead.id}
                className="group hover:bg-notion-sidebar transition-colors h-16 cursor-pointer"
                onClick={() => openLeadDrawer(lead)}
            >
                <td className="px-4 w-1/3">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                            <span className="font-medium text-notion-text text-sm">{lead.fullName || lead.name}</span>
                            {lead.archived && (
                                <span className="text-[10px] bg-stone-200 text-stone-500 px-1.5 py-0.5 rounded">Archived</span>
                            )}
                        </div>
                        <span className="text-xs text-stone-400 mt-0.5">{lead.email}</span>
                    </div>
                </td>

                <td className="px-4 w-40">
                    {(() => {
                        if (executions.length === 0) {
                            return (
                                <div className="flex items-center gap-1.5 text-[10px] text-stone-400">
                                    <span>No automation</span>
                                </div>
                            );
                        }
                        const primary = executions[0];
                        const statusLabel = primary.status === 'paused' ? 'Paused' : 'Active';
                        const statusColor = primary.status === 'paused'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-green-100 text-green-700';
                        return (
                            <div className="flex flex-col gap-1 w-32">
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] font-medium text-stone-600 truncate">
                                        {primary.automationName}
                                    </span>
                                    {executions.length > 1 && (
                                        <span className="text-[9px] text-stone-400 flex-shrink-0">+{executions.length - 1}</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${statusColor}`}>
                                        {statusLabel}
                                    </span>
                                    <span className="text-[9px] text-stone-400">
                                        {primary.currentStepIndex + 1}/{primary.totalSteps}
                                    </span>
                                </div>
                            </div>
                        );
                    })()}
                </td>
                <td className="px-4 w-1/5">
                    <div className="text-xs text-stone-600 truncate max-w-[180px]">
                        {(() => {
                            const props = lead.interestedProperties || [];
                            if (props.length === 0) {
                                return <span className="text-stone-400 italic">—</span>;
                            }
                            const firstAddress = props[0]?.address || 'Property';
                            if (props.length === 1) {
                                return firstAddress;
                            }
                            return `${firstAddress} (+${props.length - 1})`;
                        })()}
                    </div>
                </td>
                <td className="px-4">
                    <div className="flex gap-2 overflow-hidden">
                        {(lead.tags || []).map((tag, index) => (
                            <span
                                key={index}
                                className="text-[10px] text-stone-500 bg-tag-bg px-2 py-1 rounded border border-stone-200"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>
                </td>
                <td
                    className="px-4 text-right text-xs"
                    onClick={async (e) => {
                        e.stopPropagation();
                        if (!user?.uid) return;
                        try {
                            const convo = await getOrCreateConversation(user.uid, lead);
                            navigate('/inbox', { state: { openConversationId: convo.id } });
                        } catch (err) {
                            console.error('Error navigating to chat:', err);
                        }
                    }}
                >
                    <span className={`${lead._lastContactAt ? 'text-stone-400 hover:text-blue-600 hover:underline cursor-pointer' : 'text-stone-300 italic'} transition-colors`}>
                        {lead._lastContactAt ? formatMessageTime(lead._lastContactAt) : 'No contact yet'}
                    </span>
                </td>
                <td className="px-4 text-right relative" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                        <button
                            className="p-2 hover:bg-stone-200 rounded text-stone-400 hover:text-stone-700 transition"
                            onClick={async (e) => {
                                e.stopPropagation();
                                if (!user?.uid) return;
                                try {
                                    const convo = await getOrCreateConversation(user.uid, lead);
                                    navigate('/inbox', { state: { openConversationId: convo.id } });
                                } catch (err) {
                                    console.error('Error navigating to chat:', err);
                                }
                            }}
                        >
                            <MessageCircle className="w-4 h-4" />
                        </button>
                        <button
                            className="p-2 hover:bg-stone-200 rounded text-stone-400 hover:text-stone-700 transition"
                            onClick={(e) => toggleMenu(e, lead.id)}
                        >
                            <MoreHorizontal className="w-4 h-4" />
                        </button>
                    </div>
                    {openMenu === lead.id && (
                        <div className="absolute right-0 top-8 w-36 bg-white border border-notion-border shadow-popover rounded-lg z-20 py-1 text-left">
                            <button
                                className="w-full text-left px-3 py-1.5 hover:bg-notion-sidebar text-stone-600 cursor-pointer text-xs flex items-center gap-2"
                                onClick={handleEdit}
                            >
                                <Pencil className="w-3.5 h-3.5" /> Edit
                            </button>
                            <button
                                className="w-full text-left px-3 py-1.5 hover:bg-notion-sidebar text-stone-600 cursor-pointer text-xs flex items-center gap-2"
                                onClick={handleArchive}
                            >
                                {lead.archived ? (
                                    <>
                                        <ArchiveRestore className="w-3.5 h-3.5" /> Unarchive
                                    </>
                                ) : (
                                    <>
                                        <Archive className="w-3.5 h-3.5" /> Archive
                                    </>
                                )}
                            </button>
                            <div className="h-px bg-notion-border my-1"></div>
                            <button
                                className="w-full text-left px-3 py-1.5 hover:bg-red-50 text-red-600 cursor-pointer text-xs flex items-center gap-2"
                                onClick={handleDeleteClick}
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                        </div>
                    )}
                </td>
            </tr>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <tr>
                    <td colSpan={6}>
                        <div className="fixed inset-0 bg-stone-900/20 flex items-center justify-center z-50" onClick={(e) => e.stopPropagation()}>
                            <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full mx-4">
                                <h3 className="text-lg font-semibold text-stone-800 mb-2">Delete Lead?</h3>
                                <p className="text-sm text-stone-600 mb-4">
                                    This will permanently delete <strong>{lead.fullName || lead.name}</strong>. This action cannot be undone.
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowDeleteConfirm(false);
                                        }}
                                        className="flex-1 py-2 border border-notion-border rounded text-stone-600 text-sm font-medium hover:bg-stone-50 transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDelete}
                                        disabled={isDeleting}
                                        className="flex-1 py-2 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition"
                                    >
                                        {isDeleting ? 'Deleting...' : 'Delete'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

export default LeadRow;
